import React, { useCallback, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  ButtonGroup,
  Paper,
  Chip,
  Stack
} from '@mui/material';
import ScaleIcon from '@mui/icons-material/Scale';
import useScaleWeight from '../../../../hooks/useScaleWeight';
import { ScaleReading, ScaleTarget, readScaleWeight } from '../../../../services/localScaleService';

interface WeightInputModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (weight: number, mode: 'add' | 'update') => void;
  selectedProduct: {
    productInfo: {
      id: string;
      name: string;
      price: number;
      vatRate: number;
      unit: string;
    },
    itemCanBeNegative: boolean;
    itemStockQuantity: number;
    currentQuantity?: number;
  } | null;
  isCartUpdate?: boolean;
  /** local-service adresi; tartı bağlantısı bunun üzerinden kurulur. */
  localServiceUrl?: string | null;
  /**
   * Ekran ayarlarında seçili tartı. Null ise (tartı kullanılmıyor ya da
   * seçilmemiş) modal eskisi gibi tamamen elle çalışır.
   */
  scaleTarget?: ScaleTarget | null;
  /**
   * "live": pencere açıkken ağırlık izlenir, oturunca kendiliğinden dolar.
   * "single": pencere açılınca bir kez okunur; sürekli trafik istenmeyen
   * kurulumlar için.
   */
  scaleReadMode?: string;
}

const WeightInputModal: React.FC<WeightInputModalProps> = ({ 
  open, 
  onClose, 
  onConfirm, 
  selectedProduct,
  isCartUpdate = false,
  localServiceUrl = null,
  scaleTarget = null,
  scaleReadMode = 'live'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [mode, setMode] = useState<'add' | 'update'>('update');
  // Kullanıcı elle değer girdiyse tartı okuması artık üzerine yazmaz
  const [manualEntry, setManualEntry] = useState(false);

  const isSingleMode = scaleReadMode === 'single';

  // Tartı yoklaması yalnızca modal açıkken çalışır; kapalıyken local-service
  // bağlantıyı kapatıp cihaza trafik göndermeyi bırakır.
  const live = useScaleWeight({
    enabled: open && !isSingleMode,
    target: scaleTarget,
    localServiceUrl
  });

  // Tek sorgu modu: pencere açılınca bir kez okunur, sonrası kasiyerin
  // isteğine bağlı. SSE hiç açılmadığı için tartıya sürekli trafik gitmez.
  const [singleReading, setSingleReading] = useState<ScaleReading | null>(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleError, setSingleError] = useState<string | null>(null);

  const readOnce = useCallback(async () => {
    if (!scaleTarget) return;
    setSingleLoading(true);
    setSingleError(null);
    try {
      setSingleReading(await readScaleWeight(scaleTarget, localServiceUrl));
    } catch (err: any) {
      setSingleReading(null);
      setSingleError(err?.message || 'Tartı okunamadı');
    } finally {
      setSingleLoading(false);
    }
  }, [scaleTarget, localServiceUrl]);

  useEffect(() => {
    if (!open || !isSingleMode) {
      setSingleReading(null);
      setSingleError(null);
      return;
    }
    readOnce();
  }, [open, isSingleMode, readOnce]);

  const reading = isSingleMode ? singleReading : live.reading;
  // Tek okumada kararlılık bilgisi yok; gelen değer doğrudan kullanılır.
  const isStable = isSingleMode ? !!singleReading : live.isStable;
  const scaleActive = !!scaleTarget && (isSingleMode || live.status === 'connected' || live.status === 'connecting');
  const scaleKg = reading?.kg ?? null;

  // Modal açıldığında input değerini ayarla
  useEffect(() => {
    if (open) {
      // Eğer sepet güncellemesiyse mevcut miktarı göster
      if (isCartUpdate && selectedProduct?.currentQuantity !== undefined) {
        setInputValue(selectedProduct.currentQuantity.toString());
        setManualEntry(true); // mevcut miktar korunmalı, tartı ezmesin
      } else {
        setInputValue('');
        setManualEntry(false);
      }
      setIsProcessing(false);
      setMode('update'); // Varsayılan mod: güncelle
    }
  }, [open, selectedProduct, isCartUpdate]);

  // Ağırlık oturduğunda miktarı kendiliğinden doldur. Kullanıcı elle bir şey
  // yazdıysa dokunulmaz; kasiyerin girdiği değeri tartının ezmesi en can sıkıcı
  // davranış olurdu.
  useEffect(() => {
    if (!open || manualEntry || isProcessing) return;
    if (isStable && scaleKg !== null && scaleKg > 0) {
      setInputValue(String(scaleKg));
    }
  }, [open, manualEntry, isProcessing, isStable, scaleKg]);

  const handleTakeFromScale = () => {
    if (scaleKg === null || scaleKg <= 0) return;
    setInputValue(String(scaleKg));
    setManualEntry(false);
  };

  const handleNumpadClick = (value: string) => {
    if (value !== 'Enter') setManualEntry(true);

    if (value === 'C') {
      setInputValue('');
      setManualEntry(false); // temizlendi: tartı yeniden doldurabilir
    } else if (value === '<') {
      setInputValue(prev => prev.slice(0, -1));
    } else if (value === 'Enter') {
      handleConfirm();
    } else if (value === '.' || value === ',') {
      // Nokta veya virgül tuşuna basıldığında
      if (!inputValue.includes('.')) {
        setInputValue(prev => prev + '.');
      }
    } else if (/[0-9]/.test(value)) {
      setInputValue(prev => prev + value);
    }
  };

  const handleConfirm = () => {
    if (inputValue && parseFloat(inputValue) > 0 && !isProcessing) {
      setIsProcessing(true); // İşlem başladı, butonları devre dışı bırak
      const weightValue = Math.max(0.01, parseFloat(inputValue) || 0.01);
      console.log('Miktar değeri onaylandı:', weightValue, 'Mod:', mode);
      onConfirm(weightValue, mode);
    }
  };

  const title = isCartUpdate 
    ? "Ürün Miktarını Güncelle" 
    : "Kg Ürün Miktarı Girin";

  const unitText = selectedProduct?.productInfo.unit 
    ? selectedProduct.productInfo.unit 
    : 'Kg';

  return (
    <Dialog 
      open={open} 
      onClose={() => {
        if (!isProcessing) {
          onClose();
        }
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        {title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {selectedProduct?.productInfo.name || 'Ürün'}
          </Typography>
          
          {isCartUpdate && selectedProduct?.currentQuantity !== undefined && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Mevcut miktar: {selectedProduct.currentQuantity} {unitText}
            </Typography>
          )}

          {/* Canlı tartı paneli — yalnızca tartı tanımlıysa görünür.
              Tanımlı değilse modal eskisi gibi tamamen elle çalışır. */}
          {scaleActive && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                bgcolor: isStable ? 'success.50' : 'action.hover',
                borderColor: isStable ? 'success.main' : 'divider'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <ScaleIcon color={isStable ? 'success' : 'action'} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Tartı
                  </Typography>
                  <Typography variant="h5" sx={{ lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                    {(isSingleMode ? singleLoading : live.status === 'connecting') && !reading
                      ? '...'
                      : `${(scaleKg ?? 0).toFixed(reading?.decimals ?? 3)} kg`}
                  </Typography>
                  {singleError && (
                    <Typography variant="caption" color="error">
                      {singleError}
                    </Typography>
                  )}
                </Box>
                <Stack spacing={0.5} alignItems="flex-end">
                  {isSingleMode ? (
                    <Button size="small" onClick={readOnce} disabled={singleLoading}>
                      {singleLoading ? 'Okunuyor...' : 'Yeniden Oku'}
                    </Button>
                  ) : (
                    <Chip
                      size="small"
                      color={isStable ? 'success' : 'default'}
                      label={isStable ? 'Sabit' : 'Ölçülüyor'}
                    />
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleTakeFromScale}
                    disabled={isProcessing || scaleKg === null || scaleKg <= 0}
                  >
                    Tartıdan Al
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}
          
          <TextField
            fullWidth
            label={`Miktar (${unitText})`}
            type="text"
            value={inputValue}
            onChange={(e) => {
              // Sadece sayı ve nokta girişine izin ver
              const value = e.target.value;
              if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                setInputValue(value);
                setManualEntry(value !== '');
              }
            }}
            inputProps={{ 
              inputMode: "decimal",
              pattern: "[0-9]*\\.?[0-9]*"
            }}
            sx={{ mb: 2 }}
          />
          
          {isCartUpdate && (
            <ButtonGroup fullWidth variant="outlined" sx={{ mb: 2 }}>
              <Button 
                color={mode === 'update' ? 'primary' : 'inherit'}
                variant={mode === 'update' ? 'contained' : 'outlined'}
                onClick={() => setMode('update')}
                disabled={isProcessing}
              >
                Miktarı Güncelle
              </Button>
              <Button 
                color={mode === 'add' ? 'primary' : 'inherit'}
                variant={mode === 'add' ? 'contained' : 'outlined'}
                onClick={() => setMode('add')}
                disabled={isProcessing}
              >
                Miktar Ekle
              </Button>
            </ButtonGroup>
          )}
          
          {/* Numpad */}
          <Grid container spacing={1}>
            {['7', '8', '9'].map(num => (
              <Grid item xs={4} key={num}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={() => handleNumpadClick(num)}
                  disabled={isProcessing}
                >
                  {num}
                </Button>
              </Grid>
            ))}
            {['4', '5', '6'].map(num => (
              <Grid item xs={4} key={num}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={() => handleNumpadClick(num)}
                  disabled={isProcessing}
                >
                  {num}
                </Button>
              </Grid>
            ))}
            {['1', '2', '3'].map(num => (
              <Grid item xs={4} key={num}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={() => handleNumpadClick(num)}
                  disabled={isProcessing}
                >
                  {num}
                </Button>
              </Grid>
            ))}
            <Grid item xs={4}>
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={() => handleNumpadClick('.')}
                disabled={isProcessing}
              >
                .
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={() => handleNumpadClick('0')}
                disabled={isProcessing}
              >
                0
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button 
                fullWidth 
                variant="outlined" 
                color="error"
                onClick={() => handleNumpadClick('C')}
                disabled={isProcessing}
              >
                C
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          color="inherit"
          disabled={isProcessing}
        >
          İptal
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="primary" 
          variant="contained"
          disabled={!inputValue || parseFloat(inputValue) <= 0 || isProcessing}
        >
          {isCartUpdate ? (mode === 'update' ? 'Güncelle' : 'Ekle') : 'Ekle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeightInputModal; 